import type {
  FloorPlanData,
  FloorPlanSnapshotTableData,
  FloorPlanTableData,
} from '@shared/types/floorPlans';
import { type CancellationPolicy } from '@shared/types/reservations';
import {
  RestaurantEventAddOn,
  RestaurantEventDisclaimer,
} from '@shared/types/restaurantEvents';
import {
  type RestaurantEvent,
  RestaurantEventTier,
} from '@shared/types/restaurants';
import { Shift } from '@shared/types/shifts';
import { sample } from 'lodash-es';

export const cancellationPolicyFactory = (
  options: Partial<CancellationPolicy> = {},
): CancellationPolicy => ({
  fee: 1000,
  hoursBeforeReservation: 120,
  id: 'cancellation-policy-id',
  threshold: 1000,
  ...options,
});

export const FALLBACK_ICON = 'Square22A';

export const FALLBACK_ICON_NAMES: string[] = [
  'NoTableChairC',
  'Round22A',
  'Square00A',
  'Oval00A',
  'Rectangle00A',
];

export const floorPlanTableFactory = (
  options: Partial<FloorPlanTableData> = {},
): FloorPlanTableData => ({
  iconName: sample(FALLBACK_ICON_NAMES) || 'NoTableChairC',
  iconWidthScale: '1.00',
  id: 'floor-plan-table-id',
  left: '10.00',
  maximumGuests: 4,
  minimumGuests: 1,
  name: 'big table',
  orientation: '0.00',
  listings: [],
  top: '100.00',
  turnTime: 90,
  rating: 0,
  ...options,
});

export const floorPlanSnapshotTableFactory = (
  options: Partial<FloorPlanSnapshotTableData> = {},
): FloorPlanSnapshotTableData => ({
  iconName: sample(FALLBACK_ICON_NAMES) || 'NoTableChairC',
  iconWidthScale: '1.00',
  id: 'floor-plan-table-id',
  isHighlighted: false,
  left: '10.00',
  name: 'big table',
  orientation: '0.00',
  top: '100.00',
  ...options,
});

export const floorPlanDataFactory = (
  options: Partial<FloorPlanData> = {},
): FloorPlanData => ({
  id: 'floor-plan-id',
  backgroundSrc: 'my-background.svg',
  floorPlanTables: [floorPlanTableFactory()],
  name: 'and floor plan was his name-o',
  ...options,
});

export const restaurantEventTierFactory = (
  options: Partial<RestaurantEventTier> = {},
): RestaurantEventTier => ({
  id: 'uuid-uuid-uuid-uuid',
  name: 'General Admission',
  price: 2000,
  capacity: 100,
  description: null,
  floorPlanTableIds: [],
  ticketsSold: 0,
  ...options,
});

export const restaurantEventFactory = (
  options: Partial<RestaurantEvent> = {},
  shiftOptions: Partial<Shift> = {},
): RestaurantEvent => ({
  id: 'restaurant-event-id',
  name: 'F1 Dinner',
  isDeleted: false,
  description: 'Come hang out, eat good food, and watch fast cars',
  location: null,
  directions: null,
  customConfirmationMessage: null,
  includeTicketsLinkInConfirmation: true,
  bookingUnit: 'seat',
  guestEmailRequirement: 'hidden',
  serviceChargePercentage: 0.2,
  capacity: 10,
  urlSlug: null,
  shift: {
    id: 'shift-id',
    startDate: '2025-05-06',
    endDate: '2025-05-06',
    startTime: '13:30:00',
    endTime: '21:00:00',
    name: 'My Event Shift',
    repeat: ['0', '1', '2', '3', '4', '5', '6'],
    occurrences: [],
    isRecurring: false,
    restaurantId: 'restaurant-id',
    ...shiftOptions,
  },
  images: [],
  disclaimers: [],
  tiers: [],
  addOns: [],
  ...options,
});

export const restaurantEventDisclaimerFactory = (
  options: Partial<RestaurantEventDisclaimer> = {},
): RestaurantEventDisclaimer => ({
  id: 'disclaimer-id',
  title: '21+',
  description: null,
  ...options,
});

export const restaurantEventAddOnFactory = (
  options: Partial<RestaurantEventAddOn> = {},
): RestaurantEventAddOn => ({
  id: 'add-on-id',
  name: 'Champagne Bottle',
  description: 'A bottle of champagne',
  price: 5000,
  ...options,
});
